import type { ComponentProps } from "react";
import { generatePath, Link } from "react-router-dom";
import { tv } from "tailwind-variants";
import { t } from "ttag";

import { usePortalUserStore } from "@/app/stores";
import { BILLING_ROUTE } from "@/features/billing/router";
import type { SelectedProduct } from "@/features/products/context";
import {
  DISABLED_REASON,
  getDisabledReasonLabel,
} from "@/features/products/models";
import { CATEGORY_SLUG } from "@/shared/domains";
import {
  ArrowRightUpIcon,
  Badge,
  Dialog,
  Label,
  MarkdownContent,
  ShowcaseCard,
} from "@/shared/ui";

const styles = tv({
  slots: {
    root: "group h-full gap-3 border-secondary-100 bg-gradient-to-l from-secondary-50 to-secondary-50 transition-colors duration-300 hover:border-secondary-100 data-[state=checked]:[&_[data-slot=product-card-label]]:text-white data-[state=checked]:[&_[data-slot=showcase-card-description]]:text-white data-[state=checked]:[&_[data-slot=showcase-card-title]]:text-white data-[state=checked]:[&_[data-slot=showcase-card-title]]:duration-300",
    inset: "relative",
    header: "grow",
    disabledOverlay:
      "absolute inset-0 z-10 bg-gradient-to-b from-neutral-800/80 from-40% to-neutral-950 to-100% opacity-0 transition-opacity duration-300 group-hover:opacity-80",
    disabledText:
      "absolute inset-x-0 bottom-11 z-20 flex flex-col gap-1 px-2 text-center text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 [&_strong]:text-sm",
    badgesContainer: "absolute top-0 left-0 p-4",
    description: "line-clamp-2 text-neutral-700",
    content: "opacity-100!",
    label:
      "inline-flex w-full cursor-pointer! items-center justify-between text-sm font-medium text-primary-950 hover:underline",
    arrowIcon: "size-4 cursor-pointer! [&_*]:cursor-pointer",
  },
  variants: {
    category: {
      [CATEGORY_SLUG.MICRODOSING]: {
        root: "data-[state=checked]:from-tasman-400 data-[state=checked]:to-tasman-700",
      },
      [CATEGORY_SLUG.LONGEVITY]: {
        root: "data-[state=checked]:from-bismark-400 data-[state=checked]:to-bismark-800",
      },
      [CATEGORY_SLUG.WEIGHT_LOSS]: {
        root: "data-[state=checked]:from-secondary-500 data-[state=checked]:to-secondary-800",
      },
      [CATEGORY_SLUG.TESTOSTERONE]: {
        root: "data-[state=checked]:from-merlot-800 data-[state=checked]:to-merlot-950",
      },
      [CATEGORY_SLUG.HAIR_GROWTH]: {
        root: "",
      },
    },
  },
});

const {
  root,
  inset,
  header,
  disabledOverlay,
  disabledText,
  badgesContainer,
  description,
  content,
  label,
  arrowIcon,
} = styles();

type CompactProductCardProps = Omit<
  ComponentProps<typeof ShowcaseCard.Root>,
  "className"
> & {
  product: SelectedProduct;
};

export const CompactProductCard = ({
  product,
  disabled,
  ...props
}: CompactProductCardProps) => {
  const isBlackFridayActive = usePortalUserStore(
    (state) => state.isBlackFridayActive,
  );

  const disabledReason = product.disabled?.[0];
  const disabledReasonLabel =
    disabledReason && getDisabledReasonLabel(disabledReason.type);

  const canUpgrade =
    disabledReason?.type === DISABLED_REASON.ALREADY_HAS_SUBSCRIPTION &&
    Boolean(disabledReason.metadata.subscriptionId) &&
    isBlackFridayActive;

  return (
    <ShowcaseCard.Root
      disabled={disabled}
      className={root({ category: product.category.slug })}
      {...props}
    >
      <ShowcaseCard.Inset className={inset()}>
        <ShowcaseCard.Thumbnail
          src={product.thumbnail ?? "#"}
          className="h-32"
        />

        {disabled && disabledReason && (
          <>
            <div className={disabledOverlay()} />

            <div className={disabledText()}>
              <p>{t`Not available`}</p>
              <strong>{disabledReasonLabel}</strong>

              {canUpgrade && (
                <Link
                  to={generatePath(BILLING_ROUTE.UPGRADE_SUBSCRIPTION, {
                    subscriptionId: String(
                      disabledReason.metadata.subscriptionId,
                    ),
                  })}
                  className="text-base font-semibold underline"
                >
                  {t`Upgrade`}
                </Link>
              )}
            </div>
          </>
        )}

        <ShowcaseCard.Badges className={badgesContainer()}>
          {product.tags.map((tag) => (
            <Badge key={tag.label} size="medium" color="light-primary">
              {tag.label}
            </Badge>
          ))}

          {product.priceTag && (
            <Badge size="medium" color="light-primary">
              {product.priceTag}
            </Badge>
          )}
        </ShowcaseCard.Badges>
      </ShowcaseCard.Inset>

      <ShowcaseCard.Header className={header()}>
        <ShowcaseCard.Title>{product.name}</ShowcaseCard.Title>
        <ShowcaseCard.Description className={description()}>
          <MarkdownContent>{product.description}</MarkdownContent>
        </ShowcaseCard.Description>
      </ShowcaseCard.Header>

      <ShowcaseCard.Content className={content()}>
        <Dialog.Trigger
          asChild
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Label data-slot="product-card-label" size="sm" className={label()}>
            {t`Learn more`}

            <ArrowRightUpIcon className={arrowIcon()} />
          </Label>
        </Dialog.Trigger>
      </ShowcaseCard.Content>
    </ShowcaseCard.Root>
  );
};
